import {AxiosError} from "axios";

const GENERIC_MESSAGE = 'Une erreur est survenue. Veuillez réessayer.';

/**
 * First non-empty string found in a validation bag, whose values are either
 * messages or arrays of messages.
 */
const firstMessageOf = (bag: unknown): string | null => {
    if (typeof bag !== 'object' || bag === null || Array.isArray(bag)) {
        return null;
    }

    for (const value of Object.values(bag)) {
        if (typeof value === 'string' && value.length) {
            return value;
        }

        const firstEntry = Array.isArray(value)
            ? value.find((entry) => typeof entry === 'string' && entry.length)
            : undefined;

        if (typeof firstEntry === 'string') {
            return firstEntry;
        }
    }

    return null;
};

/**
 * Extract a message worth showing to the pharmacist from an API error.
 *
 * Only 422 validation bags are surfaced: those come from Laravel's translated
 * rules (app locale is fr), so they read as French sentences. Everything else
 * — including `message` keys — is developer-facing English on both servers
 * ("Slot unavailable.", "No query results for model [...]") and must never
 * reach the counter.
 *
 * The bag arrives either under `errors` (Laravel default) or as the whole body
 * (DataWrappedFormRequest::failedValidation returns the bag alone).
 */
export const apiErrorMessage = (error: unknown, fallback = GENERIC_MESSAGE): string => {
    if (!(error instanceof AxiosError) || error.response?.status !== 422) {
        return fallback;
    }

    const data = error.response.data;

    return firstMessageOf(data?.errors) ?? firstMessageOf(data) ?? fallback;
};

/**
 * HTTP status of an API error, when it carries one. Callers whose remedy
 * differs per status — retry later, rephrase, wait out a rate limit — branch
 * on this rather than on a message they would have to parse.
 */
export const apiErrorStatus = (error: unknown): number | null => {
    return error instanceof AxiosError ? error.response?.status ?? null : null;
};

/**
 * 401/403 responses already produce a toast in the response interceptor both
 * clients share; callers use this to avoid stacking a second one.
 */
export const isAuthorizationError = (error: unknown): boolean => {
    return error instanceof AxiosError && [401, 403].includes(error.response?.status ?? 0);
};
